import { useCallback, useContext } from 'react';
import axios from 'axios';

import {
  CustomFlowId,
  MAGIC_STUDY_TYPE,
  MAGIC_STUDY_PRODUCT_FILE,
  Question,
  questionFieldType,
  questionSpecialIdentifiers,
} from '@ch/common/types/data';
import { AppContext } from '../../app-context';
import { useData } from '../../use-data';
import { useLang } from '../../use-lang';
import { DEFAULT_FILE_TYPE_TEXT, Endpoints, FileTypeEnum, languagesMapping, REACT_APP_ENV_URL } from '../../const';
import { emptyValue } from '../const';
import { Document } from '../types';
import { QuizContext } from '../quiz-context';
import { getFileExtension } from '../helpers/file';
import { useAnalyticsData } from './use-update-analytics-data';

export const useQuizSubmit = () => {
  const { lang } = useLang();
  const { setLoading, loadFile } = useContext(AppContext);
  const { token, intakeId, documents, setSubmitted, addNotification, pregnancyValues, pregnancyFile } = useContext(
    QuizContext,
  );
  const { dropdownData } = useContext(AppContext);
  const { updateAnalyticsData } = useAnalyticsData();

  const {
    data: {
      portal_type: portalType,
      questions,
      errors: { errorKey },
      success: { successKey },
      drugExposure,
    },
  } = useData();

  const selectStudyValue = useCallback(
    (searchIdentifer: string, value: string) => {
      const studyQuestion = questions.find(({ identifier }) => identifier === searchIdentifer)?.file;
      return dropdownData[studyQuestion]?.rawOptions.find(({ value: itemValue }) => itemValue === value);
    },
    [questions, dropdownData],
  );

  const getProductReporting = useCallback(
    async ({ values, id, additionalValue, dependsOn }) => {
      const value = values[id];
      const studySelectValue = selectStudyValue(dependsOn, values[dependsOn]);

      if (studySelectValue?.study_type === MAGIC_STUDY_TYPE) {
        return (
          (await loadFile(MAGIC_STUDY_PRODUCT_FILE)).find(({ value: itemValue }) => itemValue === value) ||
          additionalValue?.value
        );
      }

      return value;
    },
    [selectStudyValue, loadFile],
  );

  const getCountryInfo = useCallback(
    ({ values, id, file }) => {
      const fileEntry = dropdownData[
        file
      ].rawOptions.find(({ value: label, information: info, iso_code: isoCode, e2b_code: e2bCode }) =>
        [info, isoCode, e2bCode, label].includes(values[id]),
      );
      return {
        value: fileEntry.value,
        information: fileEntry.information ?? fileEntry.e2b_code ?? fileEntry.iso_code,
      };
    },
    [dropdownData],
  );

  const getRawDropdownData = useCallback(
    ({ values, id, file }) => {
      return dropdownData[file].rawOptions.find(({ value: fileValue }) => fileValue === values[id]);
    },
    [dropdownData],
  );
  const onSubmit = useCallback(
    async (values: Record<string, string | string[]>) => {
      if (!token || !intakeId) {
        return;
      }

      const currentFlow = values[questionSpecialIdentifiers.FLOW].toString();
      const currentPersona = values[questionSpecialIdentifiers.PERSON_TYPE].toString();

      const currentQuestions = questions
        .filter(q => q.personas.includes(currentPersona))
        .filter(q => q.events.includes(currentFlow));

      const isTechnicalQuestionType = (type: questionFieldType) =>
        [questionFieldType.DUPLICATOR, questionFieldType.LABEL].includes(type);

      const isSkippingNecessary = (question: Question) =>
        [CustomFlowId.AGE_UNIT.toString()].includes(question.identifier);

      const getPregnancyFileObject = () => {
        if (pregnancyFile) {
          return [
            {
              fileName: pregnancyFile.name,
              fileType: FileTypeEnum.GLOBAL,
              fileSize: pregnancyFile.size,
              compression: '',
              // eslint-disable-next-line @typescript-eslint/camelcase
              doc_description: DEFAULT_FILE_TYPE_TEXT,
            },
          ];
        }

        if (Object.keys(pregnancyValues).length === 0) {
          return [];
        }

        const filePath = questions.find(({ identifier }) => identifier === CustomFlowId.PREGNANCY_FORM)?.file;
        const fileName = filePath.split('/').pop();

        return [
          {
            fileName: fileName,
            fileType: FileTypeEnum.GLOBAL,
            fileSize: 9744931,
            compression: '',
            // eslint-disable-next-line @typescript-eslint/camelcase
            doc_description: DEFAULT_FILE_TYPE_TEXT,
          },
        ];
      };

      const questionsNormalized = currentQuestions
        .filter(el => el.field_type === questionFieldType.DUPLICATOR)
        .reduce((acc, item) => {
          return acc.map(el => {
            if (item.value.includes(el.identifier)) {
              return {
                ...el,
                duplicatorGroup: `${item.identifier}_0`,
              };
            }
            return el;
          });
        }, currentQuestions);
      // we keep all questions here because their submit lambda is not ready to accept only questions related to a particular event
      const data = await questionsNormalized
        .filter(question => !isSkippingNecessary(question))
        .reduce(
          async (
            result,
            {
              identifier: id,
              question: name,
              depends_on: dependsOn,
              field_type: type,
              additional_value: additionalValue,
              file,
              duplicatorGroup,
            },
            index,
          ) => {
            const acc = await result;
            if (isTechnicalQuestionType(type)) {
              return acc;
            }

            let record: any = values[id] || '';

            // files
            if (type === questionFieldType.DOC_UPLOAD) {
              const questionDocs: Document[] = documents[id] ?? [];
              record = JSON.stringify([
                // a bit stupid to attach pregnancy to all upload docs; it works well in case of 1 doc_upload per form [doc_info]
                ...(id === CustomFlowId.DOC_INFO ? getPregnancyFileObject() : []),
                ...questionDocs.map(el => ({
                  fileName: el.file.name,
                  fileSize: el.file.size,
                  fileType: el.values[`${id}_fileType`] || DEFAULT_FILE_TYPE_TEXT,
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  doc_description: el.values[`${id}_fileReport`] || DEFAULT_FILE_TYPE_TEXT,
                  compression: getFileExtension(el.file.name) === 'zip' ? 'zip' : '',
                })),
              ]);
            }

            //suppose to have patient_age.unit separate question
            if (id === CustomFlowId.PATIENT_AGE && values[id]) {
              const unitE2B = dropdownData[
                questions.find(q => q.identifier === CustomFlowId.AGE_UNIT)?.file
              ]?.rawOptions?.find(({ value }) => value === values[CustomFlowId.AGE_UNIT])?.e2b_code;

              const unitValue = values[CustomFlowId.AGE_UNIT];

              record = {
                value: values[id],
                unit: unitE2B || unitValue || '',
              };
            }

            //COVID additional_events logic: split; only if it checkbox set
            if (id === CustomFlowId.ADDITIONAL_EVENTS && type === questionFieldType.CHECKBOX) {
              record = (values[id] as string[])?.join(',') ?? '';
            }

            // select field flow { value, label } instead of just value
            if (values[id] && file && Object.keys(dropdownData).includes(file)) {
              record = new RegExp(/_country$/).test(id)
                ? getCountryInfo({ values, id, file })
                : getRawDropdownData({ values, id, file });
            }

            // hardcoded product_reporting product send logic
            if (
              new RegExp(`${CustomFlowId.PRODUCT_REPORTING}(_.*[0-9])*$`).test(id) &&
              values[id] !== emptyValue &&
              dependsOn &&
              values[dependsOn]
            ) {
              record = await getProductReporting({ dependsOn, values, id, additionalValue });
            }

            const idWithoutGroup = id.replace(/_\d+$/, '');

            if (duplicatorGroup) {
              acc[duplicatorGroup] = {
                ...acc[duplicatorGroup],
                [idWithoutGroup]: record,
              };
            } else {
              acc[id] = record;
            }

            acc.questionsList = {
              ...acc.questionsList,
              [`${(index + 1).toString().padStart(2, '0')}:${idWithoutGroup}`]: name,
            };

            return acc;
          },
          Promise.resolve({
            questionsList: {},
            selectedLang: languagesMapping[lang] || lang,
            flow: currentFlow,
            reporter: currentPersona,
            persona: currentPersona,
            // Typing error same as on previous source code
            // eslint-disable-next-line @typescript-eslint/camelcase
            ack_Id: intakeId,
            // TODO add files
            // eslint-disable-next-line @typescript-eslint/camelcase
            meta: {
              // Typing error same as on previous source code
              // eslint-disable-next-line @typescript-eslint/camelcase
              portal_Type: portalType,
            },
          }),
        );

      const result = Object.keys(data).reduce((acc, key) => {
        if (key.match(/_\d+$/)) {
          const newKey = key.replace(/_\d+$/, '');
          if (!Array.isArray(acc[newKey])) {
            acc[newKey] = [];
          }
          acc[newKey] = [...acc[newKey], data[key]];
        } else {
          acc[key] = data[key];
        }
        return acc;
      }, {});

      /**
       * Study Field Issue
       */
      if (result['report_select']) {
        result['report_select'].name = result?.['report_select']?.value;
        result['report_select']['study_no'] = result?.['report_select']?.e2b_code;
      }

      /**
       * Pregnancy Field Issue
       */
      if (result && result['is_pregnant'] === 'Yes') {
        const updatedEventInfo =
          result['event_info'].length &&
          result['event_info'].map(eventInfo => {
            return {
              ...eventInfo,
              // eslint-disable-next-line @typescript-eslint/camelcase
              event_reference: `${eventInfo.event_reference}\n${drugExposure}`,
            };
          });
        result['event_info'] = [...updatedEventInfo];
      }

      setLoading(true);
      await axios
        .post(
          `${REACT_APP_ENV_URL}${Endpoints.SUBMIT}?intakeId=${intakeId}`,
          {
            body: { ...result },
          },
          {
            headers: {
              token,
            },
          },
        )
        .then(() => {
          updateAnalyticsData({ abandonedField: 'NULL' }, intakeId);
          addNotification({ title: successKey, type: 'success' });
          setSubmitted(true);
        })
        .catch(() => {
          addNotification({ title: errorKey, type: 'error' });
        })
        .finally(() => setLoading(false));
    },
    [
      addNotification,
      documents,
      dropdownData,
      errorKey,
      getProductReporting,
      getCountryInfo,
      getRawDropdownData,
      intakeId,
      lang,
      portalType,
      questions,
      setLoading,
      setSubmitted,
      successKey,
      token,
      pregnancyValues,
      pregnancyFile,
      updateAnalyticsData,
      drugExposure,
    ],
  );

  const onError = (errors: Record<string, string[]>) => {
    // eslint-disable-next-line no-console
    console.log(errors);
    const nonEmptyError = Object.keys(errors).find(error => error.length > 0);
    const domNode = document.querySelector(`div [data=${nonEmptyError}]`);
    domNode?.scrollIntoView({ block: 'center', inline: 'start' });
  };

  return {
    selectStudyValue,
    onSubmit,
    onError,
  };
};
